import { DevtoolsCallbacks } from '../devtools/maybeLoadDevtools';

export type ChangeOrigin =
  | 'empty'
  | 'poll'
  | 'resolver-update'
  | 'invalidate'
  | 'invalidate-all'
  | 'refetch'
  | 'set-state'
  | 'cascade'
  | 'init'
  | 'materialize'
  | 'schema-change'
  | 'gc'
  | 'retain'
  | 'peer-sync'
  | 'visibility'
  | 'resource-removed';

export type ChangeContext = {
  changeId: number;
  originId: number;
  origin: ChangeOrigin;
  canCascade: boolean;
  optimistic: boolean;
};

export const emptyChangeContext: ChangeContext = {
  changeId: -1,
  originId: -1,
  origin: 'empty',
  canCascade: true,
  optimistic: false,
};

export const createChangeManager = ({
  devtools,
}: {
  devtools?: DevtoolsCallbacks;
} = {}) => {
  let nextChangeId = 1;

  return {
    start: (
      origin: ChangeOrigin = 'empty',
      { optimistic = false }: { optimistic?: boolean } = {}
    ): ChangeContext => {
      const id = nextChangeId++;
      const changeContext: ChangeContext = {
        originId: id,
        changeId: id,
        origin,
        canCascade: true,
        optimistic,
      };

      devtools?.traceOrigin(
        origin,
        changeContext,
        new Error().stack?.split('\n').slice(0, 10).join('\n')
      );

      return changeContext;
    },
    cascade: (from: ChangeContext): ChangeContext => ({
      changeId: nextChangeId++,
      originId: from.originId,
      origin: from.origin,
      canCascade: false,
      optimistic: from.optimistic,
    }),
    next: (from: ChangeContext): ChangeContext => ({
      changeId: nextChangeId++,
      originId: from.originId,
      origin: from.origin,
      canCascade: from.canCascade,
      optimistic: from.optimistic,
    }),
  };
};

export type ChangeManager = ReturnType<typeof createChangeManager>;
